import {
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { FieldArray, FormikProps } from 'formik'
import {
  InstallmentSchedule,
  GCPInstallments,
  PrivateOffer,
  GCPOfferPlanDimesnion,
} from '../../../../../modules/privateOffer/reducer'

import { useCallback, useEffect } from 'react'

import {
  DatePicker,
  LabraToolTip,
  TextInput,
  labraUIDateFormat,
} from '@labrav/react-components'
import { DateTime } from 'luxon'
import { AddUpdateButton } from '@labrav/marketplace'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './InstallmentsPrivateOffer.styles'
import { useFlagValue } from '@labrav/flags'
import { Product } from '../../../../../modules/productsListing/reducer'
import clsx from 'clsx'
import { getDimensionFromProduct } from '../../../../../modules/privateOffer/privateOfferCreation/utils/dimensionHelper'
import { InfoOutlined } from '@mui/icons-material'
import { get } from 'lodash'

type InstallmentsPrivateOfferProps = {
  formik: FormikProps<PrivateOffer>
  product: Product
  now?: () => DateTime
}

const getInstallmentsBasedOnSchedule = (
  tcv: number,
  type: InstallmentSchedule,
  durationInMonths: number,
  metricPricing: GCPOfferPlanDimesnion[],
  now: () => DateTime = DateTime.now
) => {
  let monthMultiplier = 0
  switch (type) {
    case 'monthly':
      monthMultiplier = 1
      break
    case 'quarterly':
      monthMultiplier = 3
      break
    case 'yearly':
      monthMultiplier = 12
      break
  }
  const quantityOfInstallments = Math.ceil(durationInMonths / monthMultiplier)
  const amountPerInstallment = Math.floor(tcv / quantityOfInstallments)
  const lastAmount = tcv - (quantityOfInstallments - 1) * amountPerInstallment
  return Array.from({
    length: type === 'custom' ? 1 : quantityOfInstallments,
  }).map((_, index) => ({
    flatFeePrice:
      index === quantityOfInstallments - 1 ? lastAmount : amountPerInstallment,
    installmentDate: now()
      .plus({ month: index * monthMultiplier })
      .toFormat('y-MM-dd'),
    metricPricing,
  }))
}

export const InstallmentsPrivateOffer: React.FC<
  InstallmentsPrivateOfferProps
> = ({ formik, now = DateTime.now, product }) => {
  const { classes } = useStyles()
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    errors: error,
    handleBlur,
  } = formik
  const { value: isCustomEnabled, loading: isFlagLoading } = useFlagValue(
    'gcpCustomInstallments'
  )
  const productPlan = product?.plans?.find(
    plan => plan.planId === values?.plan?.planId
  )
  const metricPricing = getDimensionFromProduct(productPlan)
  const handleTcvChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const schedule = values.plan?.installmentSchedule
    const newTcv = event.target.value as unknown as number
    setFieldValue('plan.tcv', newTcv)
    if (schedule && schedule !== 'custom') {
      const installments = getInstallmentsBasedOnSchedule(
        newTcv,
        schedule,
        values.plan?.serviceLength || 0,
        metricPricing,
        now
      )
      setFieldValue('plan.installmentInfo', installments)
      setFieldTouched('plan.installmentInfo', undefined)
    }
  }
  const handleInstallmentAmountChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setFieldValue(`plan.installmentInfo[${index}].flatFeePrice`, value)
      setFieldValue('plan.installmentSchedule', 'custom')
    },
    []
  )

  const handleScheduleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSchedule = event.target.value as InstallmentSchedule
    const installments = getInstallmentsBasedOnSchedule(
      values.plan?.tcv || 0,
      newSchedule,
      values.plan?.serviceLength || 0,
      metricPricing,
      now
    )
    setFieldValue('plan.installmentInfo', installments)
    setFieldValue('plan.installmentSchedule', newSchedule)
    setFieldTouched('plan.installmentInfo', undefined)
  }
  const handleInstallmentDateChange =
    (index: number) => (date: string | null) => {
      setFieldValue(`plan.installmentInfo.[${index}].installmentDate`, date)
      setFieldValue('plan.installmentSchedule', 'custom')
    }

  const addEmptyInstallment = useCallback(
    (push: (a: GCPInstallments) => void) => () => {
      const emptyInstallment: GCPInstallments = {
        flatFeePrice: 0,
        installmentDate: '',
      }
      push(emptyInstallment)
      setFieldValue('plan.installmentSchedule', 'custom')
    },
    []
  )
  const removeInstallment = useCallback(
    (remove: (index: number) => void, index: number) => () => {
      remove(index)
      setFieldValue('plan.installmentSchedule', 'custom')
    },
    []
  )
  useEffect(() => {
    //update the installments once the serviceLength changes
    const schedule = values.plan?.installmentSchedule
    if (schedule && schedule !== 'custom') {
      const installments = getInstallmentsBasedOnSchedule(
        values.plan?.tcv || 0,
        schedule,
        values.plan?.serviceLength || 0,
        metricPricing,
        now
      )
      setFieldValue('plan.installmentInfo', installments)
    }
  }, [values.plan?.serviceLength])
  if (isFlagLoading) {
    return <div data-testid="loading"></div>
  }
  return (
    <div
      data-testid="gcp-private-offer-installments"
      className={classes.container}
    >
      <div className={classes.section}>
        <div className={clsx(classes.subHeadingContainer)}>
          <Typography
            className={classes.subHeading}
            data-testid="details-subheading-gcp"
          >
            Total Contract Value
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={'Total contract value that the buyer has to pay.'}
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>
        <TextInput
          keyName="plan.tcv"
          isReadOnly={false}
          title="Add total contract value"
          isRequired={true}
          onChange={handleTcvChange}
          onBlur={handleBlur}
          type="number"
          placeHolder="Add total contract value"
          value={values.plan?.tcv || ''}
          isError={
            !!(get(touched, 'plan.tcv', false) && get(error, 'plan.tcv', false))
          }
          errorText={
            (!!get(touched, 'plan.tcv', false) &&
              get(error, 'plan.tcv', ' ')) ||
            ' '
          }
        />
        <div
          className={clsx(
            classes.subHeadingContainer,
            classes.companyDetailSubHead
          )}
        >
          <Typography
            className={classes.subHeading}
            data-testid="details-subheading-gcp"
          >
            Installment Schedule
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={
              'Add Instalments that the buyer has to pay. Recommended to use either of monthly, quarterly or yearly. Use custom if you want to charge variable amounts month on month.'
            }
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>
        <div>
          <FormControl>
            <RadioGroup
              value={values.plan?.installmentSchedule || null}
              onChange={handleScheduleChange}
              row
            >
              <FormControlLabel
                value="monthly"
                control={
                  <Radio
                    classes={{ root: classes.radio, checked: classes.checked }}
                  />
                }
                label="Monthly"
                disabled={!values.plan?.serviceLength || !values.plan?.tcv}
              />
              <FormControlLabel
                value="quarterly"
                control={
                  <Radio
                    classes={{ root: classes.radio, checked: classes.checked }}
                  />
                }
                label="Quarterly"
                disabled={!values.plan?.serviceLength || !values.plan?.tcv}
              />
              <FormControlLabel
                value="yearly"
                control={
                  <Radio
                    classes={{ root: classes.radio, checked: classes.checked }}
                  />
                }
                label="Yearly"
                disabled={!values.plan?.serviceLength || !values.plan?.tcv}
              />

              <FormControlLabel
                value="custom"
                control={
                  <Radio
                    classes={{
                      root: classes.radio,
                      checked: classes.checked,
                    }}
                  />
                }
                label={isCustomEnabled ? 'Custom' : 'Custom (Coming soon)'}
                disabled={
                  !isCustomEnabled ||
                  !values.plan?.serviceLength ||
                  !values.plan?.tcv
                }
              />
            </RadioGroup>
          </FormControl>
        </div>
        <FieldArray
          name={'plan.installmentInfo'}
          render={({ push, remove }) => (
            <div className={classes.section}>
              {values.plan?.installmentInfo?.map((installment, index) => (
                <div
                  className={classes.installmentContainer}
                  key={JSON.stringify({
                    tcv: values.plan?.tcv,
                    serviceLength: values.plan?.serviceLength,
                    schedule: values.plan?.installmentSchedule,
                    index,
                  })}
                >
                  <div className={classes.inputContainer}>
                    {index === 0 && (
                      <div className={classes.installmentItem}>
                        <TextInput
                          keyName={'installmentDate'}
                          isReadOnly={true}
                          title={`Installment Date ${index + 1}`}
                          isRequired={false}
                          isError={false}
                          errorText={undefined}
                          onChange={() => void 0}
                          placeHolder="Enter installment date"
                          value={'Immediately on approval'}
                        />
                      </div>
                    )}
                    {index !== 0 && (
                      <div className={classes.installmentItem}>
                        <DatePicker
                          format={labraUIDateFormat}
                          field={{
                            title: `Installment Date ${index + 1}`,
                            humanReadableKey: 'installmentDate',
                            id: 'plans.installmentDate',
                            defaultValue: installment.installmentDate,
                            isRequired: false,
                            isReadOnly: !isCustomEnabled,
                            dataType: 'date',
                          }}
                          key={'plans.installmentDate'}
                          handleDateChange={
                            !isCustomEnabled
                              ? () => void 0
                              : handleInstallmentDateChange(index)
                          }
                        />
                      </div>
                    )}

                    <div className={classes.installmentItem}>
                      <TextInput
                        keyName={`plan.installmentInfo[${index}].flatFeePrice`}
                        isReadOnly={!isCustomEnabled}
                        title={`Installment Fee ${index + 1}`}
                        isRequired={false}
                        isError={false}
                        errorText={undefined}
                        onChange={handleInstallmentAmountChange(index)}
                        placeHolder="Enter installment amount"
                        value={installment.flatFeePrice || ''}
                        updatedValue={installment.flatFeePrice || ''}
                      />
                    </div>
                  </div>
                  {isCustomEnabled && index !== 0 && (
                    <div>
                      <IconButton
                        // className={classes.closeIconButton}
                        onClick={removeInstallment(remove, index)}
                        data-testid={`remove-installment-${index}`}
                        size="large"
                      >
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          width={'16px'}
                          height={'18px'}
                        />
                      </IconButton>
                    </div>
                  )}
                  {isCustomEnabled && index === 0 && (
                    <div className={classes.installmentAction}>
                      {/* just an empty space to compensate for the remove button */}
                    </div>
                  )}
                </div>
              ))}
              {isCustomEnabled && (
                <div>
                  <AddUpdateButton
                    label={'Add custom installment'}
                    onClick={addEmptyInstallment(push)}
                    disabled={false}
                    icon={AddCircleOutlineIcon}
                    type={'add'}
                  />
                </div>
              )}
            </div>
          )}
        ></FieldArray>
      </div>
    </div>
  )
}
